import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  Chip,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { RuleCard } from './RuleCard';
import { Rule, RuleGroup } from './types';

interface RuleColumnProps {
  group: RuleGroup;
  groupIndex: number;
  canAddRule?: boolean;
  onAddRule: (groupIndex: number) => void;
  onRemoveRule: (groupIndex: number, ruleIndex: number) => void;
  onRemoveGroup: (groupIndex: number) => void;
  fuelsMap: Map<string, string>;
  stationsMap: Map<string, string>;
  isOnlyGroup: boolean;
}

export const RuleColumn: React.FC<RuleColumnProps> = ({
  group,
  groupIndex,
  canAddRule = true,
  onAddRule,
  onRemoveRule,
  onRemoveGroup,
  fuelsMap,
  stationsMap,
  isOnlyGroup,
}) => {
  return (
    <Paper
      elevation={0}
      sx={{
        minWidth: 320,
        maxWidth: 320,
        backgroundColor: '#f5f5f5',
        border: '2px solid #e0e0e0',
        borderRadius: 2,
        display: 'flex',
        flexDirection: 'column',
        height: 'fit-content',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 2,
          pb: 1.5,
          borderBottom: '1px solid #e0e0e0',
          backgroundColor: '#fafafa',
          borderRadius: '8px 8px 0 0',
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="subtitle1" fontWeight={600}>
              Grupo {groupIndex + 1}
            </Typography>
            <Chip
              label={`${group.rules.length} ${group.rules.length === 1 ? 'regra' : 'regras'}`}
              size="small"
              color="primary"
              variant="outlined"
              sx={{ fontSize: '0.7rem', height: 20 }}
            />
          </Box>
          {!isOnlyGroup && (
            <IconButton
              size="small"
              onClick={() => onRemoveGroup(groupIndex)}
              sx={{
                color: 'error.main',
                '&:hover': {
                  backgroundColor: 'rgba(211, 47, 47, 0.08)',
                },
              }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
          Basta atender <strong>QUALQUER UMA</strong> destas regras
        </Typography>
      </Box>

      {/* Content */}
      <Box
        sx={{
          p: 1.5,
          flexGrow: 1,
          overflowY: 'auto',
          minHeight: 100,
        }}
      >
        {group.rules.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: 100,
              color: 'text.secondary',
              textAlign: 'center',
            }}
          >
            <Typography variant="body2">
              Nenhuma regra ainda.
              <br />
              Clique em "Adicionar Regra" abaixo.
            </Typography>
          </Box>
        ) : (
          group.rules.map((rule, ruleIndex) => {
            const stationNames = rule.stationIds
              .map((id) => stationsMap.get(id) || 'Desconhecido')
              .filter(Boolean);

            return (
              <React.Fragment key={ruleIndex}>
                {ruleIndex > 0 && (
                  <Box display="flex" justifyContent="center" my={1}>
                    <Chip
                      label="OU"
                      size="small"
                      color="success"
                      sx={{ fontWeight: 700 }}
                    />
                  </Box>
                )}
                <RuleCard
                  rule={rule}
                  fuelNames={(rule.fuelIds || []).map(id => fuelsMap.get(id)).filter(Boolean) as string[]}
                  stationNames={stationNames}
                  onRemove={() => onRemoveRule(groupIndex, ruleIndex)}
                />
              </React.Fragment>
            );
          })
        )}
      </Box>

      {/* Footer */}
      <Box
        sx={{
          p: 1.5,
          pt: 1,
          borderTop: '1px solid #e0e0e0',
        }}
      >
        <Tooltip
          title={
            !canAddRule
              ? 'A regra "Pagamento Pix" é exclusiva e não permite adicionar mais regras.'
              : ''
          }
          placement="top"
          arrow
        >
          <span style={{ width: '100%' }}>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => onAddRule(groupIndex)}
              fullWidth
              size="small"
              disabled={!canAddRule}
            >
              Adicionar Regra
            </Button>
          </span>
        </Tooltip>
      </Box>
    </Paper>
  );
};
